import * as React from "react";
import { AdminPreferences, SearchInterface } from "@amzn/ask-legal-domain";
import { Header, SpaceBetween } from "@amzn/awsui-components-react";
import { AuthorFilter } from "../search-configs/AuthorFilter";
import { FileTypeFilter } from "../search-configs/FileTypeFilter";
import { LegalOnlyFilter } from "../search-configs/LegalOnlyFilter";
import { Sorting } from "../search-configs/Sorting";
import { LastUpdatedDateFilter } from "../search-configs/LastUpdatedDateFilter";
import { PaginationSetting } from "../search-configs/PaginationSetting";
import { PageLibraryFilter } from "../search-configs/PageLibraryFilter";
import { InstanceFilter } from "../search-configs/InstanceFilter";
import { useCrossInstanceSearch } from "../../../hooks/use-cross-instance-search";
import { UIModel } from "../../../model/ui-model";

interface SmartSearchFilterViewProps {
    preferences: AdminPreferences.SearchPreferences;
    queryInputState: UIModel.State<SearchInterface.SearchQueryInput>;
    authors: string[];
    pageLibraries: string[];
    availableInstances?: string[];
    hasLegalOnlyAccess: boolean;
    isSearchRunning?: boolean;
    // Shows InstanceFilter for global search
    isGlobalSearch?: boolean;
}

export const SmartSearchFilterView: React.FC<SmartSearchFilterViewProps> = ({
    preferences,
    queryInputState,
    authors,
    pageLibraries,
    availableInstances,
    hasLegalOnlyAccess,
    isSearchRunning,
    isGlobalSearch = false
}) => {
    const isCrossInstanceSearchEnabled = useCrossInstanceSearch();

    const instanceFilter = React.useMemo(() => {
        // Show InstanceFilter when instances exist and (global search or cross-instance enabled)
        if (!availableInstances?.length || (!isGlobalSearch && !isCrossInstanceSearchEnabled)) {
            return null;
        }

        return (
            <InstanceFilter
                instanceIds={availableInstances}
                queryInputState={queryInputState}
                disabled={isSearchRunning}
            />
        );
    }, [availableInstances, isCrossInstanceSearchEnabled, queryInputState, isSearchRunning]);

    return (
        <SpaceBetween size="l" direction="vertical">
            <Header variant="awsui-h1-sticky">Refine Search</Header>
            <Sorting
                queryInputState={queryInputState}
                preferences={preferences.sortPrefs}
                disabled={isSearchRunning}
            />
            {instanceFilter}
            {preferences.filterPrefs.showAuthors && (
                <AuthorFilter
                    authors={authors}
                    queryInputState={queryInputState}
                    disabled={isSearchRunning}
                />
            )}
            {preferences.filterPrefs.showFileType.length > 0 && (
                <FileTypeFilter
                    queryInputState={queryInputState}
                    disabled={isSearchRunning}
                />
            )}
            {preferences.paginationPrefs.length > 0 && (
                <PaginationSetting
                    queryInputState={queryInputState}
                    prefs={preferences.paginationPrefs}
                    disabled={isSearchRunning}
                />
            )}
            {preferences.filterPrefs.showPageLibrary && (
                <PageLibraryFilter
                    pageLibraryIds={pageLibraries}
                    queryInputState={queryInputState}
                    disabled={isSearchRunning}
                />
            )}
            {preferences.filterPrefs.showLastUpdated && (
                <LastUpdatedDateFilter
                    queryInputState={queryInputState}
                    disabled={isSearchRunning}
                />
            )}
            {preferences.filterPrefs.showPermissionGroups && (
                <LegalOnlyFilter
                    queryInputState={queryInputState}
                    hasLegalOnlyAccess={hasLegalOnlyAccess}
                    disabled={isSearchRunning}
                />
            )}
        </SpaceBetween>
    );
};
